import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ArrowSquareOut,
  CurrencyEur,
  EnvelopeSimple,
  Fire,
  Funnel,
  Globe,
  MagnifyingGlass,
  Phone,
  UsersThree,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_STYLES = {
  hot: "bg-red-50 text-red-700 border-red-200",
  qualified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  nurturing: "bg-amber-50 text-amber-700 border-amber-200",
};

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function whatsappUrl(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : "";
}

function StatCard({ label, value, icon: Icon, tone = "navy" }) {
  const tones = {
    navy: "bg-[#0B2545]/5 text-[#0B2545]",
    red: "bg-red-50 text-red-700",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };

  return (
    <div className="bg-white border border-slate-200 p-5 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="font-serif text-3xl text-[#0B2545] mt-2">
          {value}
        </div>
      </div>
      <div className={`p-3 ${tones[tone]}`}>
        <Icon size={24} weight="duotone" />
      </div>
    </div>
  );
}

export default function InternationalBuyersDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [country, setCountry] = useState("");
  const [investorType, setInvestorType] = useState("");
  const [asset, setAsset] = useState("");
  const [geography, setGeography] = useState("");
  const [investmentMin, setInvestmentMin] = useState("");
  const [investmentMax, setInvestmentMax] = useState("");
  const [source, setSource] = useState("");

  const { data: investorData = { items: [] }, isLoading } = useQuery({
    queryKey: [
      "investor-intelligence", search, statusFilter, country,
      investorType, asset, geography, investmentMin,
      investmentMax, source,
    ],
    queryFn: async () => {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (country.trim()) params.country = country.trim();
      if (investorType) params.investor_type = investorType;
      if (asset) params.asset = asset;
      if (geography.trim()) params.geography = geography.trim();
      if (investmentMin) params.investment_min = investmentMin;
      if (investmentMax) params.investment_max = investmentMax;
      if (source) params.source = source;
      return (
        await api.get("/investor-intelligence/search", { params })
      ).data;
    },
  });

  const leads = useMemo(
    () =>
      (investorData.items || []).map((item) => ({
        contact: {
          id: item.id,
          name: item.name,
          company: item.company,
          email: item.email,
          phone: item.phone,
          nationality: item.nationality,
          current_country: item.country,
          preferred_language: item.preferred_language,
          buyer_reference: item.reference,
        },
        requirement: {
          id: item.requirement_id,
          budget_min: item.investment_min,
          budget_max: item.investment_max,
          countries: item.geography?.countries,
          regions: item.geography?.regions,
          cities: item.geography?.cities,
          property_types: item.assets,
          investment_objective: item.investment_objective,
          purchase_timeframe: item.timeframe,
          financing_method: item.financing_method,
          source: item.source,
        },
        status: item.status || "nurturing",
        score: Number(item.score || 0),
      })),
    [investorData]
  );

  const counts = useMemo(() => {
    const result = {
      total: leads.length,
      hot: 0,
      qualified: 0,
      nurturing: 0,
    };
    leads.forEach(({ status }) => {
      if (status in result) result[status] += 1;
    });
    return result;
  }, [leads]);

  return (
    <div>
      <PageHeader
        title={t(
          "buyerFinder.internalTitle",
          "International Buyers"
        )}
        subtitle={t(
          "buyerFinder.internalSubtitle",
          "International buyer leads, qualification and property requirements"
        )}
        actions={
          <Button
            onClick={() =>
              window.open(
                "/international-buyers/international",
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="rounded-sm bg-[#0B2545] hover:bg-[#173B63] text-white"
          >
            <ArrowSquareOut size={17} className="mr-2" />
            {t("buyerFinder.openPublicForm", "Open public form")}
          </Button>
        }
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard
          label={t("buyerFinder.totalLeads", "Total leads")}
          value={counts.total}
          icon={UsersThree}
        />
        <StatCard
          label={t("buyerFinder.hotLeads", "Hot")}
          value={counts.hot}
          icon={Fire}
          tone="red"
        />
        <StatCard
          label={t("buyerFinder.qualifiedLeads", "Qualified")}
          value={counts.qualified}
          icon={Globe}
          tone="green"
        />
        <StatCard
          label={t("buyerFinder.nurturingLeads", "Nurturing")}
          value={counts.nurturing}
          icon={Funnel}
          tone="amber"
        />
      </div>

        <div className="bg-white border border-slate-200 p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={20} className="text-[#C5A059]" />
            <h2 className="font-serif text-xl text-[#0B2545]">
              {t("buyerFinder.investorSearch", "Search investors")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-3 text-slate-400"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t(
                  "buyerFinder.searchInvestors",
                  "Name, company, email or reference"
                )}
                className="pl-10 rounded-sm"
              />
            </div>

            <Input
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              placeholder={t("buyerFinder.country", "Investor country")}
              className="rounded-sm"
            />

            <select
              value={investorType}
              onChange={(event) => setInvestorType(event.target.value)}
              className="h-10 border border-slate-200 bg-white px-3 text-sm rounded-sm"
            >
              <option value="">
                {t("buyerFinder.allInvestorTypes", "All investor types")}
              </option>
              <option value="Family Office">
                {t("buyerFinder.familyOffice", "Family office")}
              </option>
              <option value="Fund">
                {t("buyerFinder.investmentFund", "Investment fund")}
              </option>
              <option value="Developer">
                {t("buyerFinder.developer", "Developer")}
              </option>
              <option value="Private Investor">
                {t("buyerFinder.privateInvestor", "Private investor")}
              </option>
            </select>

            <select
              value={asset}
              onChange={(event) => setAsset(event.target.value)}
              className="h-10 border border-slate-200 bg-white px-3 text-sm rounded-sm"
            >
              <option value="">
                {t("buyerFinder.allAssets", "All asset classes")}
              </option>
              <option value="Hotel">{t("buyerFinder.hotel", "Hotel")}</option>
              <option value="Residential">
                {t("buyerFinder.residential", "Residential")}
              </option>
              <option value="Development">
                {t("buyerFinder.development", "Development")}
              </option>
              <option value="Student Housing">
                {t("buyerFinder.studentHousing", "Student housing")}
              </option>
              <option value="Senior Living">
                {t("buyerFinder.seniorLiving", "Senior living")}
              </option>
              <option value="Logistics">
                {t("buyerFinder.logistics", "Logistics")}
              </option>
              <option value="Luxury Residential">
                {t("buyerFinder.luxuryResidential", "Luxury residential")}
              </option>
            </select>

            <Input
              value={geography}
              onChange={(event) => setGeography(event.target.value)}
              placeholder={t(
                "buyerFinder.targetGeography",
                "Target geography"
              )}
              className="rounded-sm"
            />

            <Input
              type="number"
              min="0"
              value={investmentMin}
              onChange={(event) => setInvestmentMin(event.target.value)}
              placeholder={t(
                "buyerFinder.investmentMin",
                "Minimum investment (€)"
              )}
              className="rounded-sm"
            />

            <Input
              type="number"
              min="0"
              value={investmentMax}
              onChange={(event) => setInvestmentMax(event.target.value)}
              placeholder={t(
                "buyerFinder.investmentMax",
                "Maximum investment (€)"
              )}
              className="rounded-sm"
            />

            <select
              value={source}
              onChange={(event) => setSource(event.target.value)}
              className="h-10 border border-slate-200 bg-white px-3 text-sm rounded-sm"
            >
              <option value="">
                {t("buyerFinder.allSources", "All sources")}
              </option>
              <option value="International Buyer Finder">
                {t("buyerFinder.sourcePanatlantik", "Panatlantik website")}
              </option>
              <option value="Referral">
                {t("buyerFinder.sourceReferral", "Referral")}
              </option>
              <option value="Direct">
                {t("buyerFinder.sourceDirect", "Direct")}
              </option>
              <option value="JamesEdition">
                {t("buyerFinder.sourceJamesEdition", "JamesEdition enquiry")}
              </option>
              <option value="Preqin">
                {t("buyerFinder.sourcePreqin", "Preqin")}
              </option>
              <option value="PitchBook">
                {t("buyerFinder.sourcePitchBook", "PitchBook")}
              </option>
            </select>

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSearch("");
                setCountry("");
                setInvestorType("");
                setAsset("");
                setGeography("");
                setInvestmentMin("");
                setInvestmentMax("");
                setSource("");
                setStatusFilter("");
              }}
              className="rounded-sm"
            >
              {t("buyerFinder.clearFilters", "Clear filters")}
            </Button>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-10 border border-slate-200 bg-white px-3 text-sm rounded-sm"
            >
              <option value="">
                {t("buyerFinder.allClassifications", "All classifications")}
              </option>
              <option value="hot">{t("buyerFinder.hotLeads", "Hot")}</option>
              <option value="qualified">
                {t("buyerFinder.qualifiedLeads", "Qualified")}
              </option>
              <option value="nurturing">
                {t("buyerFinder.nurturingLeads", "Nurturing")}
              </option>
            </select>
          </div>
        </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" />
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white border border-slate-200 py-16 px-6 text-center">
          <UsersThree
            size={44}
            weight="duotone"
            className="mx-auto text-slate-300"
          />
          <h2 className="font-serif text-2xl text-[#0B2545] mt-4">
            {t("buyerFinder.noLeads", "No international buyer leads yet")}
          </h2>
          <p className="text-slate-500 mt-2">
            {t(
              "buyerFinder.noLeadsText",
              "New registrations from the public Buyer Finder will appear here automatically."
            )}
          </p>
        </div>
      ) : (
        <div className="grid xl:grid-cols-2 gap-4">
          {leads.map(({ contact, requirement, status, score }) => {
            const wa = whatsappUrl(contact.phone);

            return (
              <article
                key={contact.id}
                className="bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-slate-400">
                      {contact.campaign_country ||
                        contact.current_country ||
                        t("buyerFinder.international", "International")}
                    </div>
                    <h2 className="font-serif text-2xl text-[#0B2545] truncate mt-1">
                      {contact.name}
                    </h2>
                    <div className="text-sm text-slate-500 mt-1">
                      {[contact.nationality, contact.current_country]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <span
                      className={`inline-flex border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
                        STATUS_STYLES[status] || STATUS_STYLES.nurturing
                      }`}
                    >
                      {t(`buyerFinder.status_${status}`, status)}
                    </span>
                    <div className="text-2xl font-semibold text-[#0B2545] mt-2">
                      {score}
                      <span className="text-xs text-slate-400">/100</span>
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-5 gap-y-3 mt-5 pt-5 border-t border-slate-100 text-sm">
                  <Info
                    label={t("buyerFinder.budget", "Budget")}
                    value={`${money(requirement.budget_min)} – ${money(
                      requirement.budget_max
                    )}`}
                  />
                  <Info
                    label={t("buyerFinder.locations", "Preferred locations")}
                    value={
                      requirement.cities ||
                      requirement.regions ||
                      "—"
                    }
                  />
                  <Info
                    label={t("buyerFinder.propertyType", "Property type")}
                    value={requirement.property_types || "—"}
                  />
                  <Info
                    label={t("buyerFinder.timeframe", "Purchasing timeframe")}
                    value={requirement.purchase_timeframe || "—"}
                  />
                  <Info
                    label={t("buyerFinder.financing", "Financing")}
                    value={contact.financing_method || "—"}
                  />
                  <Info
                    label={t("buyerFinder.purpose", "Purpose")}
                    value={contact.purchase_purpose || "—"}
                  />
                  <Info
                    label={t("buyerFinder.referralCode", "Referral code")}
                    value={
                      contact.referral_code ||
                      requirement.referral_code ||
                      "—"
                    }
                  />
                  <Info
                    label={t("buyerFinder.language", "Language")}
                    value={
                      contact.preferred_language ||
                      requirement.preferred_language ||
                      "—"
                    }
                  />
                </div>

                <div className="flex flex-wrap gap-2 mt-5">
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="inline-flex items-center h-9 px-3 border border-slate-200 text-sm text-[#0B2545] hover:bg-slate-50"
                    >
                      <EnvelopeSimple size={16} className="mr-1.5" />
                      Email
                    </a>
                  )}

                  {wa && (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center h-9 px-3 border border-slate-200 text-sm text-[#0B2545] hover:bg-slate-50"
                    >
                      <Phone size={16} className="mr-1.5" />
                      WhatsApp
                    </a>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => navigate("/requirements")}
                    className="h-9 rounded-none ml-auto"
                  >
                    <CurrencyEur size={16} className="mr-1.5" />
                    {t("buyerFinder.viewRequirement", "View requirement")}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="text-slate-700 mt-0.5 break-words">{value}</div>
    </div>
  );
}
